/**
 * PasswordManagerService
 * Centralized layer for recipient-password operations.
 * Keeps API calls and crypto logic out of UI components.
 */

import {
  getRecipientPasswords,
  createRecipientPassword,
  updateRecipientPassword,
} from "../api/passwordDatabaseApi";
import {
  encryptRecipientPassword,
  decryptRecipientPassword,
} from "../utils/crypto";

/**
 * Normalizes a list of email addresses into a canonical recipient-set key.
 * trim → lowercase → distinct → sort → join ";"
 *
 * @param {string[]} emailArray
 * @returns {string}
 */
export function normalizeRecipients(emailArray) {
  const unique = [
    ...new Set(emailArray.map((e) => e.trim().toLowerCase()).filter(Boolean)),
  ];
  unique.sort();
  return unique.join(";");
}

/**
 * Fetches all recipient-password entries and decrypts them client-side.
 *
 * @param {string} oid
 * @param {string} passwortDatenbankId
 * @param {Uint8Array} masterKey
 * @returns {Promise<Array<{ id: string, email: string, password: string }>>}
 */
export async function fetchAndDecryptAll(oid, passwortDatenbankId, masterKey) {
  const result = await getRecipientPasswords(oid, passwortDatenbankId);
  if (!result.success) return [];
  const encryptedEntries = result.data || [];

  const results = [];
  for (const entry of encryptedEntries) {
    try {
      const decrypted = await decryptRecipientPassword(
        entry.encryptedEmpfaengerEmail,
        entry.encryptedPasswort,
        entry.iv,
        masterKey,
      );
      results.push({
        id: entry.empfaengerPasswortId,
        email: decrypted.email,
        password: decrypted.password,
      });
    } catch (err) {
      console.warn("[PasswordManagerService] Failed to decrypt entry:", entry.empfaengerPasswortId, err);
    }
  }

  return results;
}

/**
 * Finds a decrypted entry matching the given recipient-set key.
 *
 * @param {Array<{ id: string, email: string, password: string }>} decryptedEntries
 * @param {string} recipientSetKey - Normalized "a@x.com;b@y.com"
 * @returns {{ id: string, password: string } | null}
 */
export function findByRecipientSetKey(decryptedEntries, recipientSetKey) {
  if (!decryptedEntries || !recipientSetKey) return null;
  const found = decryptedEntries.find((e) => e.email === recipientSetKey);
  return found ? { id: found.id, password: found.password } : null;
}

/**
 * Encrypts a recipient password and creates or updates the database entry.
 *
 * @param {string} oid
 * @param {string} passwortDatenbankId
 * @param {Uint8Array} masterKey
 * @param {string} recipientSetKey - Normalized recipient email string (used as "email" field)
 * @param {string} password - Plaintext password to store
 * @param {string|null} existingId - If set, updates; otherwise creates
 * @returns {Promise<void>}
 */
export async function encryptAndSave(
  oid,
  passwortDatenbankId,
  masterKey,
  recipientSetKey,
  password,
  existingId = null,
) {
  const encrypted = await encryptRecipientPassword(recipientSetKey, password, masterKey);

  if (existingId) {
    const updateResult = await updateRecipientPassword({
      oid,
      empfaengerPasswortId: existingId,
      encryptedEmpfaengerEmail: encrypted.encryptedEmail,
      encryptedPasswort: encrypted.encryptedPassword,
      iv: encrypted.iv,
    });
    if (!updateResult.success) {
      throw new Error(updateResult.message || "Fehler beim Aktualisieren des Passworts");
    }
  } else {
    const createResult = await createRecipientPassword({
      oid,
      passwortDatenbankId,
      encryptedEmpfaengerEmail: encrypted.encryptedEmail,
      encryptedPasswort: encrypted.encryptedPassword,
      iv: encrypted.iv,
    });
    if (!createResult.success) {
      throw new Error(createResult.message || "Fehler beim Erstellen des Passworts");
    }
  }
}
